import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Manages recently searched terms in localStorage.
 */
class RecentlySearched {
  static #STORAGE_KEY = 'recentlySearchedTerms';
  static #MAX_TERMS = 10;

  /**
   * @param {string} searchTerm - The search term to add.
   */
  static addTerm(searchTerm) {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    if (!normalizedTerm) return;

    let searchedTerms = this.getTerms();
    searchedTerms = searchedTerms.filter((/** @type {string} */ term) => term !== normalizedTerm);
    searchedTerms.unshift(normalizedTerm);
    searchedTerms = searchedTerms.slice(0, this.#MAX_TERMS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(searchedTerms));
  }

  static clearTerms() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  static getTerms() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlySearchedWrapper] - The recently searched terms wrapper.
 * @property {HTMLElement[]} [recentlySearchedTitle] - The recently searched title elements.
 * @property {HTMLElement} [recentlySearchedTerms] - The recently searched terms container.
 * @property {HTMLElement} [recommendedProductsWrapper] - The recommended products wrapper.
 * @property {HTMLElement[]} [recommendedProductsTitle] - The recommended products title elements.
 * @property {HTMLElement} [recommendedProductsTerms] - The recommended products container.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {HTMLElement | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('click', this.#handleResultClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles clicks on search results to save the search term
   * @param {MouseEvent} event - The mouse event
   */
  #handleResultClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const resultCard = target.closest('.predictive-search-results__card, .predictive-search-product-card');
    const viewAllButton = target.closest('[data-button-type="view-all"]');

    if (resultCard || viewAllButton) {
      const searchTerm = this.refs.searchInput.value.trim();
      if (searchTerm) {
        RecentlySearched.addTerm(searchTerm);
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog open event.
   */
  #handleDialogOpen = () => {
    // Always try to render recently searched terms and recommended keywords when dialog opens with empty input
    const tryRender = () => {
      if (!this.refs.searchInput.value.trim()) {
        this.#renderRecentlySearchedTerms();
        this.#renderRecommendedKeywords();
      }
    };

    // Try multiple times to ensure DOM is ready
    requestAnimationFrame(() => {
      tryRender();
      // Also try after a small delay as backup
      setTimeout(tryRender, 100);
    });
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Handle autocomplete acceptance with Tab or ArrowRight
    const autocompleteElement = this.querySelector('[ref="searchAutocomplete"]');
    const suggestion = autocompleteElement?.dataset.suggestion;

    if (suggestion && this.#currentIndex === -1) {
      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        this.refs.searchInput.value = suggestion;
        // Clear autocomplete and trigger new search
        autocompleteElement.textContent = '';
        delete autocompleteElement.dataset.suggestion;
        this.#getSearchResults(suggestion);
        return;
      }

      if (event.key === 'ArrowRight') {
        // Only accept if cursor is at the end of the input
        const input = this.refs.searchInput;
        if (input.selectionStart === input.value.length) {
          event.preventDefault();
          this.refs.searchInput.value = suggestion;
          // Clear autocomplete and trigger new search
          autocompleteElement.textContent = '';
          delete autocompleteElement.dataset.suggestion;
          this.#getSearchResults(suggestion);
          return;
        }
      }
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft') {
      return;
    }

    // Don't handle ArrowRight if we're navigating results
    if (event.key === 'ArrowRight' && this.#currentIndex === -1) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          // Save search term before navigating
          const searchTerm = this.refs.searchInput.value.trim();
          if (searchTerm) {
            RecentlySearched.addTerm(searchTerm);
          }
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          // Save search term when clicking on a result
          const searchTerm = this.refs.searchInput.value.trim();
          if (searchTerm) {
            RecentlySearched.addTerm(searchTerm);
          }
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // Save search term when viewing all results
          const searchTerm = this.refs.searchInput.value.trim();
          if (searchTerm) {
            RecentlySearched.addTerm(searchTerm);
          }
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', searchTerm);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently searched terms.
   * @param {Event} event - The event.
   */
  clearRecentlySearched(event) {
    event.stopPropagation();

    RecentlySearched.clearTerms();

    const { recentlySearchedWrapper } = this.refs;

    if (recentlySearchedWrapper) {
      recentlySearchedWrapper.classList.add('removing');

      onAnimationEnd(recentlySearchedWrapper, () => {
        recentlySearchedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      // Clear autocomplete immediately
      const autocompleteElement = this.querySelector('[ref="searchAutocomplete"]');
      if (autocompleteElement) {
        autocompleteElement.textContent = '';
        delete autocompleteElement.dataset.suggestion;
      }
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 150);

  /**
   * Updates the "View All" button text with the search term
   * @param {string} searchTerm - The current search term
   */
  #updateViewAllButton(searchTerm) {
    const viewAllButton = this.querySelector('[data-button-type="view-all"]');
    if (viewAllButton instanceof HTMLElement && searchTerm) {
      // Get the translation string from Theme object or use a fallback
      const translationKey = Theme.translations?.search_results_view_all_for || 'View all Search Results for "{{ query }}"';

      // Create a temporary textarea to safely decode HTML entities
      const tempTextarea = document.createElement('textarea');
      tempTextarea.innerHTML = translationKey;
      const decodedTemplate = tempTextarea.value || translationKey;

      const buttonText = decodedTemplate.replace('{{ query }}', searchTerm);

      // Update only the text span to preserve the icon
      const textSpan = viewAllButton.querySelector('.predictive-search__search-button-text');
      if (textSpan) {
        textSpan.textContent = buttonText;
      }
    }

    // Update collection button if a collection is found in results
    this.#updateCollectionButton();

    // Render recommended keywords in search results
    this.#renderRecommendedKeywordsInSearch();
  }

  /**
   * Updates the collection button based on search results
   */
  #updateCollectionButton() {
    const collectionButton = this.querySelector('[data-button-type="collection"]');
    const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');

    if (!collectionButton || !resultsInner) return;

    const collectionTitle = resultsInner.dataset.firstCollectionTitle;
    const collectionUrl = resultsInner.dataset.firstCollectionUrl;

    if (collectionTitle && collectionUrl) {
      // Show and update the collection button
      const textSpan = collectionButton.querySelector('.predictive-search__search-button-text');
      if (textSpan) {
        // Get the translation string from Theme object or use a fallback
        const translationKey = Theme.translations?.view_collection || 'View {{ collection }}';
        // Create a temporary textarea to safely decode HTML entities
        const tempTextarea = document.createElement('textarea');
        tempTextarea.innerHTML = translationKey;
        const decodedTemplate = tempTextarea.value || translationKey;
        const buttonText = decodedTemplate.replace('{{ collection }}', collectionTitle);
        textSpan.textContent = buttonText;
      }
      collectionButton.href = collectionUrl;
      collectionButton.style.display = '';
    } else {
      // Hide the collection button if no collection found
      collectionButton.style.display = 'none';
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        // Update autocomplete BEFORE morph for faster response
        this.#updateAutocomplete(searchTerm, resultsMarkup);

        morph(predictiveSearchResults, resultsMarkup);

        // Highlight search term in product cards
        this.#highlightSearchTerm(searchTerm);

        this.#updateViewAllButton(searchTerm);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Updates the autocomplete suggestion based on search results markup
   * @param {string} searchTerm - The current search term
   * @param {string} resultsMarkup - The HTML markup of search results
   */
  #updateAutocomplete(searchTerm, resultsMarkup) {
    const autocompleteElement = this.querySelector('[ref="searchAutocomplete"]');
    if (!autocompleteElement) return;

    // Parse the markup to find the first query suggestion quickly
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    const firstQuery = tempDiv.querySelector('.predictive-search-results__card--query a');

    if (!firstQuery) {
      requestAnimationFrame(() => {
        autocompleteElement.textContent = '';
        delete autocompleteElement.dataset.suggestion;
      });
      return;
    }

    // Get the full suggested text from the link
    const fullSuggestion = firstQuery.getAttribute('aria-label') || firstQuery.textContent.trim();

    // Check if the suggestion starts with the search term (case insensitive)
    const searchTermLower = searchTerm.toLowerCase();
    const suggestionLower = fullSuggestion.toLowerCase();

    if (suggestionLower.startsWith(searchTermLower) && fullSuggestion.length > searchTerm.length) {
      // Create invisible text for what user typed + visible suggestion
      const typedPart = searchTerm;
      const suggestionPart = fullSuggestion.substring(searchTerm.length);

      // Update DOM in the next frame for smooth rendering
      requestAnimationFrame(() => {
        // Safely insert user input using textContent/createTextNode to prevent XSS
        autocompleteElement.textContent = '';
        const span = document.createElement('span');
        span.style.color = 'transparent';
        span.textContent = typedPart;
        autocompleteElement.appendChild(span);
        autocompleteElement.appendChild(document.createTextNode(suggestionPart));
        autocompleteElement.dataset.suggestion = fullSuggestion;
      });
    } else {
      requestAnimationFrame(() => {
        autocompleteElement.textContent = '';
        delete autocompleteElement.dataset.suggestion;
      });
    }
  }

  /**
   * Highlights the search term in product card titles and descriptions
   * @param {string} searchTerm - The current search term
   */
  #highlightSearchTerm(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) return;

    const productCards = this.querySelectorAll('.predictive-search-product-card');
    const searchTermLower = searchTerm.toLowerCase();

    productCards.forEach((card) => {
      // Highlight in title
      const title = card.querySelector('.predictive-search-product-card__title');
      if (title && title.textContent) {
        const originalText = title.textContent;
        const lowerText = originalText.toLowerCase();
        const index = lowerText.indexOf(searchTermLower);

        if (index !== -1) {
          const before = originalText.substring(0, index);
          const match = originalText.substring(index, index + searchTerm.length);
          const after = originalText.substring(index + searchTerm.length);
          // Safely insert product title content using DOM manipulation to prevent XSS
          title.textContent = '';
          title.appendChild(document.createTextNode(before));
          const span = document.createElement('span');
          span.style.fontWeight = '700';
          span.textContent = match;
          title.appendChild(span);
          title.appendChild(document.createTextNode(after));
        }
      }

      // Highlight in description
      const description = card.querySelector('.predictive-search-product-card__description');
      if (description && description.textContent) {
        const originalText = description.textContent;
        const lowerText = originalText.toLowerCase();
        const index = lowerText.indexOf(searchTermLower);

        if (index !== -1) {
          const before = originalText.substring(0, index);
          const match = originalText.substring(index, index + searchTerm.length);
          const after = originalText.substring(index + searchTerm.length);
          // Safely insert product description content using DOM manipulation to prevent XSS
          description.textContent = '';
          description.appendChild(document.createTextNode(before));
          const span = document.createElement('span');
          span.style.fontWeight = '700';
          span.textContent = match;
          description.appendChild(span);
          description.appendChild(document.createTextNode(after));
        }
      }
    });
  }

  /**
   * Renders the recently searched terms as pills
   */
  #renderRecentlySearchedTerms() {
    // Re-query the DOM to get fresh refs after morph
    const recentlySearchedWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recentlySearchedWrapper"]')
    );
    const recentlySearchedTerms = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recentlySearchedTerms"]')
    );

    if (!recentlySearchedTerms || !recentlySearchedWrapper) {
      return;
    }

    const searchedTerms = RecentlySearched.getTerms();

    if (searchedTerms.length === 0) {
      recentlySearchedWrapper.style.display = 'none';
      return;
    }

    recentlySearchedWrapper.style.display = 'block';
    recentlySearchedTerms.innerHTML = '';

    searchedTerms.forEach((/** @type {string} */ term) => {
      const pill = document.createElement('a');
      pill.className = 'recently-searched-terms__pill';
      pill.href = `${Theme.routes.search_url}?q=${encodeURIComponent(term)}`;
      pill.textContent = term.charAt(0).toUpperCase() + term.slice(1);

      recentlySearchedTerms.appendChild(pill);
    });
  }

  /**
   * Renders recommended keywords from theme settings
   */
  #renderRecommendedKeywords() {
    const recommendedProductsWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recommendedProductsWrapper"]')
    );
    const recommendedProductsTerms = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recommendedProductsTerms"]')
    );

    if (!recommendedProductsTerms || !recommendedProductsWrapper) {
      return;
    }

    // Get keywords from Theme settings
    const keywordsString = Theme.settings?.recommended_keywords || '';

    if (!keywordsString.trim()) {
      recommendedProductsWrapper.style.display = 'none';
      return;
    }

    // Split by comma and trim each keyword
    const keywords = keywordsString.split(',').map((k) => k.trim()).filter((k) => k.length > 0);

    if (keywords.length === 0) {
      recommendedProductsWrapper.style.display = 'none';
      return;
    }

    // Show wrapper and render pills
    recommendedProductsWrapper.style.display = 'block';
    recommendedProductsTerms.innerHTML = '';

    keywords.forEach((keyword) => {
      const pill = document.createElement('a');
      pill.className = 'recommended-products-terms__pill';
      pill.href = `${Theme.routes.search_url}?q=${encodeURIComponent(keyword)}`;
      pill.textContent = keyword;

      recommendedProductsTerms.appendChild(pill);
    });
  }

  /**
   * Renders recommended keywords in active search results
   */
  #renderRecommendedKeywordsInSearch() {
    const recommendedProductsWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recommendedProductsWrapperSearch"]')
    );
    const recommendedProductsTerms = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="recommendedProductsTermsSearch"]')
    );

    if (!recommendedProductsTerms || !recommendedProductsWrapper) {
      return;
    }

    // Get query suggestions from search results
    const queryResults = this.refs.predictiveSearchResults.querySelectorAll('.predictive-search-results__card--query a');

    if (queryResults.length > 0) {
      // Use query suggestions from search results
      recommendedProductsWrapper.style.display = 'block';
      recommendedProductsTerms.innerHTML = '';

      queryResults.forEach((queryLink) => {
        const keyword = queryLink.getAttribute('aria-label') || queryLink.textContent.trim();
        const href = queryLink.getAttribute('href') || '';

        if (keyword && href) {
          const pill = document.createElement('a');
          pill.className = 'recommended-products-terms__pill';
          pill.href = href;
          pill.textContent = keyword;

          recommendedProductsTerms.appendChild(pill);
        }
      });
    } else {
      // Fallback to Theme settings if no query suggestions
      const keywordsString = Theme.settings?.recommended_keywords || '';

      if (!keywordsString.trim()) {
        recommendedProductsWrapper.style.display = 'none';
        return;
      }

      // Split by comma and trim each keyword
      const keywords = keywordsString.split(',').map((k) => k.trim()).filter((k) => k.length > 0);

      if (keywords.length === 0) {
        recommendedProductsWrapper.style.display = 'none';
        return;
      }

      // Show wrapper and render pills
      recommendedProductsWrapper.style.display = 'block';
      recommendedProductsTerms.innerHTML = '';

      keywords.forEach((keyword) => {
        const pill = document.createElement('a');
        pill.className = 'recommended-products-terms__pill';
        pill.href = `${Theme.routes.search_url}?q=${encodeURIComponent(keyword)}`;
        pill.textContent = keyword;

        recommendedProductsTerms.appendChild(pill);
      });
    }
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';

    // Clear autocomplete
    const autocompleteElement = this.querySelector('[ref="searchAutocomplete"]');
    if (autocompleteElement) {
      autocompleteElement.textContent = '';
      delete autocompleteElement.dataset.suggestion;
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);

    // Hide collection button when resetting search
    const collectionButton = this.querySelector('[data-button-type="collection"]');
    if (collectionButton) {
      collectionButton.style.display = 'none';
    }

    // Hide recommended keywords in search section when resetting
    const recommendedSearchWrapper = this.querySelector('[ref="recommendedProductsWrapperSearch"]');
    if (recommendedSearchWrapper) {
      recommendedSearchWrapper.style.display = 'none';
    }

    // Render recently searched terms and recommended keywords after morph
    this.#renderRecentlySearchedTerms();
    this.#renderRecommendedKeywords();

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
